/*
 * TealerLab — le pack composé apparaît comme UNE ligne de panier.
 *
 * Le pack builder ajoute N lignes marquées `_tlr_src: "pack-builder"`. Au panier, le client
 * ne doit pas voir N lignes mais un seul « produit » : titre du pack, nombre d'articles,
 * prix total (remise de palier comprise), un dépli pour le détail, et un lien pour le
 * modifier là où il a été composé.
 *
 * C'est un REGROUPEMENT D'AFFICHAGE, rien d'autre : le panier Shopify garde ses lignes à
 * l'unité, donc le stock, la remise et le checkout sont inchangés. Ne jamais essayer de
 * fusionner réellement les lignes — ce serait un produit « pack » à créer côté catalogue,
 * avec son propre stock.
 *
 * Le tiroir et la page panier partagent ce fichier : ils rendent la même ligne, avec les
 * mêmes classes, et ne peuvent pas diverger.
 */
(function () {
  'use strict';

  const PACK_SRC = 'pack-builder';
  const BUNDLE_SRC = 'bundle';
  /* Les origines qui se rendent comme UN produit. Le pack composé et le pack de la page
     d'accueil suivent la même règle ; seul le pack composé se remodifie (le bundle est un
     ensemble figé, il n'a pas de composeur). */
  const GROUPED = [PACK_SRC, BUNDLE_SRC];

  function srcOf(item) {
    return String(((item && item.properties) || {})._tlr_src || '');
  }

  function isPackLine(item) {
    return GROUPED.indexOf(srcOf(item)) >= 0;
  }

  /** Les groupes présents dans ce panier, dans l'ordre de leur première ligne. */
  function groups(items) {
    const bySrc = new Map();
    for (const it of items || []) {
      const src = srcOf(it);
      if (GROUPED.indexOf(src) < 0) continue;
      if (!bySrc.has(src)) bySrc.set(src, []);
      bySrc.get(src).push(it);
    }
    const out = [];
    for (const [src, lines] of bySrc) out.push(summarize(src, lines));
    return out;
  }

  /**
   * Un groupe tel qu'il est dans le panier. Renvoie null si aucune ligne.
   */
  function summarize(src, lines) {
    if (!lines || !lines.length) return null;
    let count = 0;
    let original = 0;
    let final = 0;
    const discounts = [];
    for (const l of lines) {
      count += l.quantity || 0;
      original += l.original_line_price || 0;
      final += l.final_line_price || 0;
      for (const a of l.line_level_discount_allocations || []) {
        const title = a && a.discount_application && a.discount_application.title;
        if (title && discounts.indexOf(title) < 0) discounts.push(title);
      }
    }
    return {
      src,
      lines,
      count,
      original,
      final,
      discounts,
      keys: lines.map((l) => l.key),
      firstKey: lines[0].key,
      image: (lines.find((l) => l.image) || {}).image || '',
    };
  }

  /** Compat : le pack composé seul, pour un appelant qui ne veut que lui. */
  function group(items) {
    return groups(items).find((g) => g.src === PACK_SRC) || null;
  }

  function fill(template, token, value) {
    return String(template || '').split('{' + token + '}').join(value);
  }

  /**
   * La ligne groupée. Elle réutilise les classes d'une ligne de panier normale pour hériter
   * de sa mise en page ; seules les parties propres au pack ont leurs propres classes.
   * `ctx.editUrl` vide = pas de lien « modifier » (la page du composeur n'existe pas).
   */
  function rowHtml(pack, ctx) {
    const esc = ctx.escapeHtml;
    const i18n = ctx.i18n || {};
    const title = (i18n.titles && i18n.titles[pack.src]) || i18n.packTitle || '';
    // Seul le pack composé se remodifie : le bundle n'a pas de composeur, donc pas de lien.
    const editUrl = pack.src === PACK_SRC ? ctx.editUrl || '' : '';
    const badge = pack.discounts.length
      ? '<span class="tlr-cart__pack-badge">' + esc(pack.discounts.join(' · ')) + '</span>'
      : '';
    const detail = pack.lines
      .map((l) => {
        const variant = l.variant_title && l.variant_title !== 'Default Title' ? ' · ' + esc(l.variant_title) : '';
        return (
          '<li class="tlr-cart__pack-line">' +
          (l.image ? '<img src="' + esc(l.image) + '" alt="" loading="lazy">' : '') +
          '<span class="tlr-cart__pack-line-name">' + esc(l.product_title || l.title || '') + variant + '</span>' +
          '<span class="tlr-cart__pack-line-qty">×' + (l.quantity || 0) + '</span>' +
          '<span class="tlr-cart__pack-line-price">' + esc(ctx.fmtMoney(l.original_line_price || 0)) + '</span>' +
          '</li>'
        );
      })
      .join('');
    const price =
      pack.original > pack.final
        ? '<div class="tlr-cart__item-price-stack">' +
          '<s class="tlr-cart__item-price-orig">' + esc(ctx.fmtMoney(pack.original)) + '</s>' +
          '<strong class="tlr-cart__item-price tlr-cart__item-price--sale">' + esc(ctx.fmtMoney(pack.final)) + '</strong>' +
          '</div>'
        : '<strong class="tlr-cart__item-price">' + esc(ctx.fmtMoney(pack.final)) + '</strong>';
    const closeSvg =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    return (
      '<article class="tlr-cart__item tlr-cart__item--pack" data-pack-group data-keys="' + esc(pack.keys.join(',')) + '">' +
      (pack.image ? '<img class="tlr-cart__item-img" src="' + esc(pack.image) + '" alt="" loading="lazy">' : '') +
      '<div class="tlr-cart__item-info">' +
      '<div class="tlr-cart__item-top">' +
      '<h4 class="tlr-cart__item-name">' + esc(title) + '</h4>' +
      '<button type="button" class="tlr-cart__item-remove" data-pack-remove aria-label="' + esc(i18n.remove || '') + '">' + closeSvg + '</button>' +
      '</div>' +
      '<span class="tlr-cart__item-variant">' + esc(fill(i18n.packItems || '', 'count', pack.count)) + badge + '</span>' +
      // Les deux actions vivent sur la MÊME ligne : empilées, elles poussaient le prix
      // en bas de la carte et donnaient deux blocs très espacés.
      '<div class="tlr-cart__pack-actions">' +
      '<button type="button" class="tlr-cart__pack-toggle" data-pack-toggle aria-expanded="false"' +
      ' data-label-show="' + esc(i18n.packShow || '') + '" data-label-hide="' + esc(i18n.packHide || '') + '">' +
      esc(i18n.packShow || '') + '</button>' +
      (editUrl
        ? '<a class="tlr-cart__pack-edit" href="' + esc(editUrl) + '">' + esc(i18n.packEdit || '') + '</a>'
        : '') +
      '</div>' +
      '<ul class="tlr-cart__pack-detail" data-pack-detail hidden>' + detail + '</ul>' +
      '<div class="tlr-cart__item-bottom">' + price + '</div>' +
      '</div>' +
      '</article>'
    );
  }

  /** Ouvre/ferme le détail. Aucun appel panier : c'est de l'affichage. */
  function toggle(button) {
    const row = button.closest('[data-pack-group]');
    const detail = row && row.querySelector('[data-pack-detail]');
    if (!detail) return;
    const open = detail.hasAttribute('hidden');
    if (open) detail.removeAttribute('hidden');
    else detail.setAttribute('hidden', '');
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    const label = open ? button.dataset.labelHide : button.dataset.labelShow;
    if (label) button.textContent = label;
  }

  window.TlrCartPack = {
    SRC: PACK_SRC,
    BUNDLE_SRC,
    GROUPED,
    srcOf,
    isPackLine,
    group,
    groups,
    rowHtml,
    toggle,
    fill,
  };
})();
